import React, {ReactNode, useReducer} from 'react';
import {ModalComponent} from 'module/Modal/component/ModalComponent';
import {createAction, createReducer} from '@reduxjs/toolkit';
import Promise from 'bluebird'
import {Button, DialogActions, DialogTitle, makeStyles, Typography} from '@material-ui/core';
import {ErrorButton} from 'module/Error/component/ErrorButton';
import {log} from 'common/log';
import {parseServerError} from 'module/Error/util/parseServerError';
import {UNKNOWN_ERROR} from 'module/ConfirmDialog/constant/UnknownError';
import {catchError} from 'rxjs/operators';
import {ObservableInput} from 'rxjs'

export enum ConfirmVariant {
	/** Ja = resolve, Nein = reject */
	JN,
	/** (default) OK = resolve */
	OK,
	/** error: OK = reject */
	ERROR,
	/** "Jetzt aktualisieren" = resolve, "abbrechen" = reject */
	REFRESH,
	/** "Weiter bearbeiten" = resolve, "Schließen" = reject */
	RETURN
}

interface AlertParams {
	title?: ReactNode
	message?: ReactNode
	variant?: ConfirmVariant
}

interface ConfirmDialogState {
	open: boolean
	message: ReactNode
	title: ReactNode
	variant: ConfirmVariant
	resolve?: (result?: any) => void
	reject?: (reason?: any) => void
}

type ActionProps = Partial<Omit<ConfirmDialogState, 'open'>>

const promisedQueue: ActionProps[] = []

const NAMESPACE = 'ConfirmDialog/'
const showAction = createAction<ActionProps>(NAMESPACE + 'show')
const closeAction = createAction(NAMESPACE + 'reset')
const dfltState = {
	open: false,
	variant: ConfirmVariant.OK,
	message: '',
	title: '',
}
const reducer = createReducer<ConfirmDialogState>(dfltState, builder => builder
	.addCase(showAction, (state, {payload}) => {
		if (state.resolve || state.reject) {
			if (!state.open) throw new Error("There is was a promise not answered");
			promisedQueue.push(payload)
			return state;
		}
		let {variant, message, title, resolve, reject} = payload
		if (variant == null) variant = ConfirmVariant.OK
		if (title == null) title = ''
		if (message == null) message = ''
		return {
			open: true,
			variant,
			message,
			title,
			resolve,
			reject,
		}
	}).addCase(closeAction, (state) => ({
		...state,
		open: false,
		resolve: undefined,
		reject: undefined,
	})),
)

const useStyles = makeStyles(theme => ({
	wrapper: {
		height: `calc(100% - ${theme.spacing(8)}px)`,
		overflow: 'auto',
		background: 'inherit',
	},
	title: {
		background: 'inherit',
		whiteSpace: 'pre-wrap',
	},
	message: {
		margin: theme.spacing(0, 3),
		whiteSpace: 'pre-wrap',
	},
	dialogActions: {
		position: 'absolute',
		right: 0,
		bottom: 0,
		width: '100%',
		background: 'inherit',
		textAlign: 'right',
	},
	button: {
		margin: theme.spacing(1, 2),
	},
}), {name: 'ConfirmDialogContainer'})

const defaultSize = {height: 300, width: 560}
let initialized = false;

// is changed after mount of ConfirmDialogContainer
// used outside of container in "Confirm"
let _showDialogCB = (params: AlertParams) => {
	const p = new Promise((resolve, reject) => {
		promisedQueue.push({...params, resolve, reject})
	})
	p.then(() => log.debug('user said yes'), () => log.debug('user said no')) //ignore uncatched error message
	return p
}

export function ConfirmDialogContainer() {
	const [state, dispatch] = useReducer(reducer, dfltState)
	if (!initialized) {
		_showDialogCB = ({title = '', message = '', variant = ConfirmVariant.OK}: AlertParams) => {
			const p = new Promise(function (resolve, reject) {
				dispatch(showAction({
					variant,
					message,
					title,
					resolve,
					reject,
				}))
			});
			p.then(() => log.debug('user said yes'), () => log.debug('user said no')) //ignore uncatched error message
			return p;
		}
		initialized = true
	}
	if (promisedQueue.length && !state.open) {
		const x = promisedQueue.shift()
		if (x) dispatch(showAction(x))
	}
	const classes = useStyles()
	const resolve = () => {
		if (!state.resolve) throw new Error("missing resolve")
		state.resolve()
		dispatch(closeAction())
	}
	const reject = () => {
		if (!state.reject) throw new Error("missing reject")
		state.reject() // no error because it is a user-initiated action (error may be shown of succeeding error handlers)
		dispatch(closeAction())
	}
	let buttons
	switch (state.variant) {
		case ConfirmVariant.JN:
			buttons = <>
				<Button variant={'contained'} className={classes.button} color={'secondary'} onClick={resolve} autoFocus>Ja</Button>
				<ErrorButton variant={'contained'} className={classes.button} onClick={reject}>Nein</ErrorButton>
			</>
			break
		case ConfirmVariant.ERROR:
			buttons = <ErrorButton variant={'contained'} className={classes.button} onClick={reject} autoFocus>OK</ErrorButton>
			break
		case ConfirmVariant.REFRESH:
			buttons = <>
				<Button variant={'contained'} className={classes.button} color={'secondary'} onClick={resolve} autoFocus>Jetzt aktualisieren</Button>
				<Button variant={'contained'} className={classes.button} onClick={reject}>Abbrechen</Button>
			</>
			break
		case ConfirmVariant.RETURN:
			buttons = <>
				<Button variant={'contained'} className={classes.button} color={'secondary'} onClick={resolve} autoFocus>Weiter bearbeiten</Button>
				<ErrorButton variant={'contained'} className={classes.button} onClick={reject}>Schließen</ErrorButton>
			</>
			break
		default:
		case ConfirmVariant.OK:
			buttons = <Button variant={'contained'} className={classes.button} onClick={resolve} color={'primary'} autoFocus>OK</Button>
			break
	}

	return <ModalComponent
		open={state.open}
		onClose={reject}
		defaultSize={defaultSize}
		closeOnBackdropClick={false}
	>
		<div className={classes.wrapper}>
			<DialogTitle className={classes.title}>{state.title}</DialogTitle>
			<Typography component={'div'} className={classes.message}>
				{state.message}
			</Typography>
		</div>
		<DialogActions className={classes.dialogActions}>
			{buttons}
		</DialogActions>
	</ModalComponent>
}

function defaultArgs(titleOrMessage?: ReactNode, message?: ReactNode, defaultTitle?: ReactNode) {
	let title;
	if (message === undefined) {
		message = titleOrMessage
		title = undefined
	} else {
		title = titleOrMessage
	}
	if (defaultTitle && title == null) title = defaultTitle
	return {title, message}
}

/**
 * all calls require ConfirmDialogContainer mounted in ReactRoot
 */

/** info: OK = resolve */
export function alertInfo(titleOrMessage: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message), variant: ConfirmVariant.OK})
}

/** error: OK = reject */
export function alertError(titleOrMessage?: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message, 'Fehler'), variant: ConfirmVariant.ERROR})
}

/** error: OK = reject */
export function alertServerError(titleOrMessage?: ReactNode, message?: ReactNode): Promise<unknown> {
	let args = defaultArgs(titleOrMessage, message, 'Fehler');
	if (!args.message) args = UNKNOWN_ERROR
	return _showDialogCB({...args, variant: ConfirmVariant.ERROR})
}

/** error: OK = reject */
export function serverErrorHandler(error?: any) {
	return alertServerError(parseServerError(error))
}

/** Server error handler for RxJS */
export function catchServerError<T extends ObservableInput<any>>(returnValue?: T) {
	return catchError((err: any) => {
		void serverErrorHandler(err)
		return returnValue ?? []
	})
}

/** ask: J = resolve, N = reject */
export function confirmJN(titleOrMessage?: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message), variant: ConfirmVariant.JN})
}

export function confirmDialog(variant: ConfirmVariant, titleOrMessage?: ReactNode, message?: ReactNode): Promise<unknown> {
	return _showDialogCB({...defaultArgs(titleOrMessage, message), variant: variant})
}